import {ProductionPatternGroup, ProductionTipItem} from './types';

export const PRODUCTION_WHEN_TO_USE: ProductionTipItem = {
    id: 'when-to-use',
    title: 'When to use a large query',
    body: 'Large queries match how customers talk, not how tickets are categorized. In production, teams usually combine wildcard stems, quoted customer phrases, proximity tuning, and exclusion lists rather than deep boolean nesting. Start with a small core, review search results, then expand phrase banks and add exclusions for false positives.',
};

export const PRODUCTION_TEMPLATE_GROUPS: ProductionPatternGroup[] = [
    {
        id: 'structural-templates',
        title: 'Structural templates',
        intro: 'Abstract patterns derived from production queries. Replace placeholders with your product names, symptoms, and customer phrases.',
        items: [
            {
                id: 'product-symptom-code',
                title: 'Product × symptom × identifier',
                structure: '(product*) AND (symptom*) AND identifier',
                query: '(remote* OR controller*) AND (battery* OR "batteries empty") AND s6',
                whenToUse: 'One device family, one problem class, optional model or SKU filter.',
                productionNote: 'Similar to remote control + empty batteries + model code queries.',
                tags: ['wildcard-stems'],
            },
            {
                id: 'topic-symptom-exclusions',
                title: 'Topic + symptoms + exclusion firewall',
                structure: '(topic* OR synonym*) AND (symptom* OR "phrase") -noise* -unrelated*',
                query: '(internet* OR connection*) AND (missing* OR "red light" OR gone*) -wifi* -tv* -mobile* -billing*',
                whenToUse: 'Broad topic that matches unrelated products unless you exclude them explicitly.',
                productionNote: 'Common for outage queries where wifi, TV, and mobile pollute results.',
                tags: ['exclusions', 'wildcard-stems'],
            },
            {
                id: 'anchor-phrase-bank',
                title: 'Product anchor + phrase synonym bank',
                structure: '(device*) AND ("phrase"~N "alternate phrase"~M ...)',
                query: '(router* OR "usb modem") AND ("firmware update"~2 "software update"~4 "need to update"~3 "update failed"~2 "download update" "install update"~5)',
                whenToUse: 'Customers describe the same issue in many ways; collect real phrases from transcripts.',
                productionNote: 'Similar to dongle/router firmware update queries with dozens of proximity-tuned phrases.',
                tags: ['phrase-bank', 'proximity', 'wildcard-stems'],
            },
            {
                id: 'phrase-or-bank',
                title: 'Pure phrase OR bank',
                structure: '"phrase one" "phrase two"~N stem* "phrase four"~M',
                query: '"suspicious call" scam* "number spoofed"~1 "blocked the calls"~13 "unwanted calls"~2 "fraud message"',
                whenToUse: 'Any single matching utterance is enough; prioritize recall of customer language.',
                productionNote: 'Similar to scam-call and authentication failure queries in production.',
                tags: ['phrase-bank', 'proximity'],
            },
            {
                id: 'product-variant-enumeration',
                title: 'Product name variant enumeration',
                structure: '"Product A" "Product A variant" "Product A inflected"~1 ...',
                query: '"Android box" "Android boxes" "Android box unit"~1 "Android set-top box" "Android tv box" "Android box device"~2',
                whenToUse: 'Spelling, branding, and inflection variants matter more than boolean logic.',
                productionNote: 'Similar to set-top box queries with long lists of product name forms.',
                tags: ['product-variants', 'phrase-bank'],
            },
            {
                id: 'dual-strategy',
                title: 'Dual strategy: stem group + phrase list',
                structure: '(stem* AND (symptom OR list)) OR "exact phrase" OR "another phrase" -noise*',
                query: '(signal* AND (weak OR poor OR problem* OR intermittent)) OR "no signal" OR "signal gone" OR "signal disappeared" OR "lost coverage" -wifi*',
                whenToUse: 'Some customers use keyword + adjective patterns; others use fixed phrases — cover both.',
                productionNote: 'Similar to mobile coverage queries combining stems with exhaustive phrase lists.',
                tags: ['dual-strategy', 'exclusions', 'wildcard-stems'],
            },
            {
                id: 'multi-branch-taxonomy',
                title: 'Multi-branch issue taxonomy',
                structure: '((component* AND symptoms) OR ("product" AND failure) OR "error phrase") -exclusion*',
                query: '((picture* OR tv* OR settop*) AND (blank OR black OR "no picture")) OR ("tv service" AND "no internet") OR "technical error occurred" -wifi*',
                whenToUse: 'One reported issue type maps to several unrelated-sounding customer descriptions.',
                productionNote: 'Similar to TV picture and connectivity queries with separate branches per symptom shape.',
                tags: ['multi-branch', 'exclusions'],
            },
            {
                id: 'loose-recall',
                title: 'Loose boolean mix (higher recall)',
                structure: '(topic* OR link*) AND (drop* OR problem*) OR glitch* -noise*',
                query: '(internet* OR connection* OR link*) AND (drop* OR problem* OR cut*) OR glitch* OR intermittent* -wifi* -hdmi*',
                whenToUse: 'Recall matters more than precision; clean up noise with exclusions afterward.',
                productionNote: 'Similar to connection-drop queries with mixed AND/OR at the top level.',
                tags: ['loose-boolean', 'exclusions', 'wildcard-stems'],
            },
        ],
    },
    {
        id: 'proximity-tuning',
        title: 'Proximity tuning in production',
        intro: 'Production queries tune ~N by phrase length. Short phrases need low slop; long natural sentences need higher slop.',
        items: [
            {
                id: 'proximity-short',
                title: 'Short customer phrase',
                query: '"not working"~1',
                description: 'One optional word inside a short phrase.',
                tags: ['proximity'],
            },
            {
                id: 'proximity-medium',
                title: 'Medium phrase',
                query: '"cannot log in"~4',
                description: 'A few extra words allowed between phrase parts.',
                tags: ['proximity'],
            },
            {
                id: 'proximity-long',
                title: 'Long natural sentence',
                query: '"how do I stop these calls"~13',
                description: 'Longer customer sentences need higher deviation values.',
                tags: ['proximity'],
            },
        ],
    },
];

export const PRODUCTION_BUILDING_BLOCKS: string[] = [
    'Product stems — remote* OR "set-top box"',
    'Symptom stems — broken* OR missing* OR problem*',
    'Exact phrases — "exact customer wording"',
    'Proximity phrases — "longer customer phrase"~N',
    'Required groups — group1 AND group2',
    'Exclusions — -wifi* -billing* -unrelated_product*',
];

export const PRODUCTION_MAINTENANCE_TIPS: ProductionTipItem[] = [
    {
        id: 'grow-incrementally',
        title: 'Grow incrementally',
        body: 'Start with (topic*) AND (symptom*). Run the search, read matching transcripts, then add quoted phrases you find. Repeat until recall is good enough.',
    },
    {
        id: 'exclusions-last',
        title: 'Add exclusions last',
        body: 'Add -term exclusions when you see false positives in results, not upfront. Production queries often have long exclusion lists built over time.',
    },
    {
        id: 'avoid-deep-nesting',
        title: 'Avoid deep nesting',
        body: 'Production queries rarely go deeper than two parentheses levels. Prefer phrase banks and exclusions over complex boolean trees.',
    },
    {
        id: 'synonym-lists',
        title: 'Plan for synonym volume',
        body: 'Product names often need 10–50 quoted variants rather than one keyword. Budget maintenance time for phrase lists, not just boolean structure.',
    },
];

export const PRODUCTION_SHOWCASE_QUERY = {
    title: 'Medium-size production-style query (abstract)',
    query: '(internet* OR connection*) AND (missing* OR "red indicator" OR "no connection" OR gone* OR "cannot connect"~3) -wifi* -tv* -mobile* -billing* -router*',
    description: 'Combines topic stems, symptom stems, a proximity phrase, and a practical exclusion list — typical shape before expanding into a full phrase bank.',
};
